import React from 'react';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileQuestion,
  Loader2,
  X,
} from 'lucide-react';
import { humanize } from '../utils/documentUtils';

const STATUS_STYLES = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  AVAILABLE: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  GENERATED: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  PROCESSING: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  GENERATING: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  PARTIAL_FAILED: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  REPLACED: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  EXPIRED: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  CANCELLED: 'bg-slate-100 text-slate-700 ring-slate-500/20',
  FAILED: 'bg-red-50 text-red-700 ring-red-600/20',
  REVOKED: 'bg-red-50 text-red-700 ring-red-600/20',
  MISSING: 'bg-red-50 text-red-700 ring-red-600/20',
  NOT_GENERATED: 'bg-gray-100 text-gray-700 ring-gray-500/20',
};

export const StatusBadge = ({ value }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
      STATUS_STYLES[value] || 'bg-gray-100 text-gray-700 ring-gray-500/20'
    }`}
  >
    {humanize(value || 'UNKNOWN')}
  </span>
);

export const PageHeader = ({ title, description, actions }) => (
  <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
    <div>
      <p className="mb-1 text-sm font-medium text-blue-600">Documents / ID Cards</p>
      <h1 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const ErrorBanner = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
      <p className="flex-1">{message}</p>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="text-red-500 hover:text-red-800">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export const LoadingBlock = ({ label = 'Loading...' }) => (
  <div className="flex min-h-48 items-center justify-center gap-3 text-sm text-gray-500">
    <Loader2 className="h-5 w-5 animate-spin" /> {label}
  </div>
);

export const EmptyState = ({ title = 'Nothing found', description }) => (
  <div className="flex min-h-56 flex-col items-center justify-center p-8 text-center">
    <div className="mb-3 rounded-full bg-gray-100 p-3 text-gray-400">
      <FileQuestion className="h-6 w-6" />
    </div>
    <h3 className="font-semibold text-gray-900">{title}</h3>
    {description && <p className="mt-1 max-w-md text-sm text-gray-500">{description}</p>}
  </div>
);

export const Modal = ({ open, onClose, title, description, children, footer, size = 'max-w-3xl' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/55 p-4">
      <div className={`flex max-h-[92vh] w-full ${size} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer && <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
};

export const Pagination = ({ pagination, page, onPageChange, disabled }) => {
  if (!pagination || pagination.total_pages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4 text-sm">
      <span className="text-gray-500">
        Page {pagination.page || page} of {pagination.total_pages} · {pagination.count} records
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || !pagination.previous}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <button
          type="button"
          disabled={disabled || !pagination.next}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
